/**
 * interviewApi — the single typed surface over the POST-AIQ-170 Interview-prep
 * backend (routes/interview_prep.py [practice] + routes/interview_projects.py
 * [projects]). Every interview component calls THROUGH here rather than
 * hand-rolling axios URLs, so the endpoint shapes, the consent-gate
 * `?student_id=` threading, and the error classification live in ONE place and
 * can't drift between the surfaces (TrackPicker / QuestionRunner / ProjectPanel).
 *
 * Mirrors career/careersApi.js deliberately — same module discipline, same
 * `makeWithStudent` `?student_id=` threading, same `friendlyError` envelope
 * reader, same `classifyCareerError` "no dead ends" branch. The interview
 * surface rides the IDENTICAL composite career gate (a minor needs a
 * guardian-granted `career_profile` consent AND a teacher sign-off; 18+
 * self-serve), so a gate failure here classifies and routes EXACTLY like the
 * rest of Career Discovery — to CareerConsentRequiredNotice → CareerAccessGate.
 *
 * THE BASE PREFIX is `/careers/interview` (the backend nests the whole
 * interview surface under the careers router prefix). The project routes hang
 * off `/careers/interview/projects` + `/careers/interview/submissions`.
 *
 * `studentId` (optional, trailing arg on every gated call): a guardian acting
 * on a verified-linked minor passes the minor's UUID, which the gate dependency
 * reads as `?student_id=`. Omitted → the caller acts as themselves (the
 * student's own self-serve view). The BACKEND enforces the link + consent; this
 * is purely plumbing the query param uniformly (the careersApi contract).
 */
import axios from 'axios';
import { API_URL } from '../../config';
import {
  friendlyError,
  makeWithStudent,
  readMessage,
} from '../../api/httpError';
// Re-use the career surface's gate classifier so a 403/404/429 from an
// interview route routes to the SAME UX state as every other career surface
// (the "no dead ends" contract — ONE branch, no drift).
import { classifyCareerError } from '../careersApi';

const BASE = `${API_URL}/api/v1/careers/interview`;

// `withStudent` + `friendlyError` are the shared envelope readers
// (frontend/src/api/httpError.js) — so career + interview thread `?student_id=`
// and read the error envelope IDENTICALLY. Re-exported so components import the
// classifiers from this ONE module.
const withStudent = makeWithStudent(BASE);
export { friendlyError, classifyCareerError, readMessage };

// A PREFETCH rides with `x-background-request: true` — the tag the global
// interceptor in App.js reads to skip auto-logout (the login-prewarm
// precedent) and that keeps a gate response from popping a modal for a
// request the student never asked for. The foreground read of the same URL
// carries no tag, so a real gate failure still surfaces where the student is.
const BACKGROUND_HEADERS = Object.freeze({ 'x-background-request': 'true' });

async function readJson(url, { background = false } = {}) {
  const res = background
    ? await axios.get(url, { headers: BACKGROUND_HEADERS })
    : await axios.get(url);
  return res.data;
}

// ── Tracks (read — no egress) ────────────────────────────────────────────────

/**
 * GET /careers/interview/tracks — the full track inventory ("Explore all").
 * Returns `{ count, tracks:[{ slug, title, track_family, summary,
 * default_scorer, is_content_complete, recommended }], next_steps:[str] }`.
 * FULL tracks lead; "coming soon" stubs are flagged `is_content_complete=false`
 * (shown but not enterable).
 */
export async function listTracks(studentId) {
  const res = await axios.get(withStudent('/tracks', studentId));
  return res.data;
}

/**
 * GET /careers/interview/tracks/recommended — the recommended picker default.
 * Wires the deterministic match engine: the student's matched-occupation tracks
 * (fit-ordered), falling back to quant-trading + consulting so it is NEVER
 * empty. Returns the same track-card shape + `assessment_pending` (true when no
 * scored profile → the two FULL tracks are the default and a nudge points at
 * the assessment) + `next_steps`. Each card carries `recommended:true`.
 */
export async function listRecommendedTracks(studentId) {
  const res = await axios.get(withStudent('/tracks/recommended', studentId));
  return res.data;
}

// ── The track hub + its prep materials (read — no egress; F28 PR3) ───────────

/**
 * GET /careers/interview/tracks/{slug}/hub — everything the hub opens with.
 * Returns `{ track (card), profile (the role profile, or null for a track the
 * library has no content for), topics:[{ slug, label, summary?, questions }],
 * guides:[{ slug, kind, title, summary, topic, estimated_minutes }] (the
 * index — bodies come from `listTrackGuides`), mocks:[{ key, title, summary,
 * duration_minutes, question_count, sections:[{ title, minutes,
 * question_count }], available }], counts:{ questions:{ total, behavioral,
 * technical }, guides, projects, mocks }, can_generate, active_mock:{
 * session_id, mock_key, started_at } | null, next_steps }`.
 *
 * `background: true` tags the request as a prefetch (a track-card hover).
 */
export async function getTrackHub(slug, studentId, options) {
  return readJson(
    withStudent(`/tracks/${encodeURIComponent(slug)}/hub`, studentId),
    options,
  );
}

/**
 * GET /careers/interview/tracks/{slug}/guides — every active guide WITH its
 * body, in the library's order: `{ track, count, guides:[{ slug, kind, title,
 * summary, topic, estimated_minutes, body_md, key_points:[str] }],
 * next_steps }`. One request makes the whole Learn tab instant; it is
 * prefetched (`background: true`) right after the hub lands.
 */
export async function listTrackGuides(slug, studentId, options) {
  return readJson(
    withStudent(`/tracks/${encodeURIComponent(slug)}/guides`, studentId),
    options,
  );
}

// ── Progress (read — no egress) ──────────────────────────────────────────────

/**
 * GET /careers/interview/progress — the student's interview-prep PERFORMANCE
 * roll-up across every track they've practiced (the "how am I doing" view that
 * sits ABOVE the track picker). A pure read of already-graded attempts +
 * project submissions — NO LLM, NO egress.
 *
 * Returns `{ overall, tracks, next_steps }`:
 *   - `overall` { tracks_practiced, total_attempts, total_graded,
 *     projects_graded, needs_review } — the headline counts.
 *   - `tracks[]` { track_slug, track_title, scorer ('objective'|'rubric'),
 *     attempts, graded, score_pct (0-100 or null), correct, avg_rubric_score,
 *     needs_review, projects_graded, project_avg_score, last_practiced_at } —
 *     one performance row per practiced track. `score_pct` is null until the
 *     track has a graded attempt (render "not yet graded", never a 0% bar).
 *     The scorer picks the label: objective → "X of Y correct"; rubric → an
 *     "avg score N/100" read.
 *   - `next_steps[]` — the standing "no dead ends" forward actions.
 *
 * EMPTY (no activity at all) ⇒ an empty `tracks` list — and THAT (not
 * `total_attempts`) is the "nothing yet" signal: a track row appears when the
 * student has >=1 attempt OR >=1 project submission, so `total_attempts` (which
 * counts only questions) can be 0 while `tracks` is non-empty (a projects-only
 * student). The caller keys its friendly "practice a question" nudge on
 * `tracks.length`, NEVER a dead end. All scores are ADVISORY growth signals
 * (warm palette), never grades.
 */
export async function getProgress(studentId) {
  const res = await axios.get(withStudent('/progress', studentId));
  return res.data;
}

// ── Questions (serve = read; generate = EGRESS) ──────────────────────────────

/**
 * GET /careers/interview/questions?track=&difficulty=&module= — serve a
 * track's ACTIVE questions (seeded first, then generated) — NO LLM. The
 * grading key is NEVER surfaced; each card carries a `scorer_kind`
 * ('objective' | 'rubric') hint so the runner picks the right answer-input
 * UX, plus a derived `module` ('behavioral' | 'technical') tag. Returns
 * `{ track, difficulty, module, count, questions:[{ id, track_slug,
 * question_type, module, prompt, difficulty, origin, scorer_kind }],
 * next_steps:[str] }`. A stub track with no content returns an empty list +
 * a "coming soon" next-step (no dead end).
 *
 * `difficulty` (optional) filters the band ('intro'|'core'|'stretch');
 * `module` (optional) filters behavioral vs technical (server-derived from
 * question_type — never stored); `topic` (optional) one of the track's topic
 * slugs (the hub's vocabulary); `limit` / `offset` page the list (the
 * response carries `total`). The response's cards also carry `title`,
 * `topic`, `rev`, `has_walkthrough`, `attempted`, `solved` (F28 D7).
 */
export async function listQuestions(
  track,
  { difficulty, module, topic, limit, offset } = {},
  studentId,
) {
  let path = `/questions?track=${encodeURIComponent(track)}`;
  if (difficulty) path += `&difficulty=${encodeURIComponent(difficulty)}`;
  if (module) path += `&module=${encodeURIComponent(module)}`;
  if (topic) path += `&topic=${encodeURIComponent(topic)}`;
  if (typeof limit === 'number') path += `&limit=${limit}`;
  if (typeof offset === 'number' && offset > 0) path += `&offset=${offset}`;
  const res = await axios.get(withStudent(path, studentId));
  return res.data;
}

/**
 * POST /careers/interview/questions/generate — generate ONE verifier-QA'd
 * question on demand (EGRESS; the under-13 platform freeze + the per-student
 * daily budget apply server-side). Body `{ track, difficulty }`.
 *
 * Returns one of two 200 shapes (NOT a dead end either way):
 *   - `{ generated:true, question:{...card}, next_steps }` — a fresh question;
 *   - `{ generated:false, question:null, next_steps }` — the verifier rejected /
 *     a post-failure cooldown; the student keeps practicing the seeded set.
 *
 * The OVER-BUDGET case is a 429 (NOT a 200): the caller must catch it and
 * render calm cooldown copy (read the friendly message via `friendlyError`),
 * never an error surface. `classifyCareerError` maps it to 'rate-limit'.
 */
export async function generateQuestion(track, { difficulty = 'core' } = {}, studentId) {
  const res = await axios.post(withStudent('/questions/generate', studentId), {
    track,
    difficulty,
  });
  return res.data;
}

// ── Sessions + attempts (session = no egress; attempt = EGRESS) ──────────────

/**
 * POST /careers/interview/sessions — open a 'practice' session on a track (a DB
 * row; no egress). Body `{ track }`. Returns `{ session_id, track, mode,
 * status, next_steps }`. The runner needs the `session_id` to submit attempts.
 * A missing/inactive track → 404.
 */
export async function createSession(track, studentId) {
  const res = await axios.post(withStudent('/sessions', studentId), { track });
  return res.data;
}

/**
 * POST /careers/interview/sessions with `mock_key` — start a MOCK interview in
 * one of the hub's formats. The server picks the questions (preferring ones
 * the student has not answered) and returns the session payload: `{
 * session_id, track, mode:'mock', status:'active', mock_key, title,
 * duration_minutes, started_at, sections:[{ title, minutes, instructions?,
 * items:[{ question (card), answered }] }], progress:{ answered, total },
 * next_steps }`. A format that cannot be fully assembled right now is a 409
 * `mock_unavailable` (nothing changes); a start racing another window is a
 * 409 `mock_start_conflict`. Starting a mock abandons the student's older
 * unfinished mock on the same track.
 */
export async function startMock(track, mockKey, studentId) {
  const res = await axios.post(withStudent('/sessions', studentId), {
    track,
    mock_key: mockKey,
  });
  return res.data;
}

/**
 * GET /careers/interview/sessions/{id} — one of the student's own sessions.
 * For a mock the shape of `startMock`; once the mock is completed (or
 * abandoned) each answered item adds `attempt:{ attempt_id, student_response,
 * grade_status, is_correct, feedback, submitted_at, question_changed,
 * evaluation? }` and, unless the question was edited since, `walkthrough` +
 * `correct_answer`; the payload adds `totals:{ questions, answered, pending,
 * objective:{ graded, correct }, rubric:{ graded, average_score },
 * needs_review }`. While `totals.pending` is above zero the debrief re-reads.
 */
export async function getSession(sessionId, studentId, { retry = true } = {}) {
  // The debrief's re-read loop passes `retry: false`: it has its own back-off
  // (a 429's Retry-After, else doubling), and the global axios-retry would
  // otherwise send up to four requests per tick against the shared per-IP
  // budget before the loop even saw the failure.
  const res = await axios.get(
    withStudent(`/sessions/${encodeURIComponent(sessionId)}`, studentId),
    retry ? undefined : { 'axios-retry': { retries: 0 } },
  );
  return res.data;
}

/**
 * POST /careers/interview/sessions/{id}/complete — finish a mock and get its
 * debrief (the `getSession` completed shape). Idempotent: a double click or a
 * retry returns the same debrief. A practice session is 409 `not_a_mock`; a
 * mock replaced by a newer one is 409 `session_abandoned`.
 */
export async function completeSession(sessionId, studentId) {
  const res = await axios.post(
    withStudent(`/sessions/${encodeURIComponent(sessionId)}/complete`, studentId),
  );
  return res.data;
}

/**
 * POST /careers/interview/sessions/{id}/attempts — submit an answer → grade it →
 * per-answer feedback (EGRESS; the under-13 freeze applies server-side). Body
 * `{ question_id, student_response, time_to_submit_ms? }`.
 *
 * Returns `{ attempt_id, session_id, question_id, grade_status, is_correct,
 * feedback, is_advisory:true, next_steps, evaluation? }`:
 *   - OBJECTIVE (quant) questions carry `is_correct` (true/false) + warm
 *     `feedback` (correct → affirm; wrong → encourage, NEVER red).
 *   - RUBRIC (consulting) questions carry `is_correct:null` + `feedback`
 *     (narrative) + an `evaluation` object { overall_score, dimension_scores,
 *     dimensions, verifier_status, needs_human_review, is_advisory }.
 *   - `grade_status` is 'graded' | 'failed' (a calm "couldn't score" — the
 *     attempt is still saved; NEVER a 500/dead end).
 *
 * `is_advisory` is always true: this is GROWTH FEEDBACK, not a score/grade.
 * `evaluation.needs_human_review` (rubric) nudges a mentor look on a close call.
 *
 * `questionRev` echoes the card's `rev` (F28 D7): an answer to a question that
 * was edited since the student opened it is refused with 409 `question_changed`
 * (carrying the fresh card) instead of being graded against content they did
 * not see. Inside an ACTIVE MOCK the response is an acknowledgement only:
 * `{ attempt_id, session_id, question_id, recorded:true, progress:{ answered,
 * total }, next_steps }` — the grade lands in the debrief.
 */
export async function submitAttempt(
  sessionId,
  { questionId, studentResponse, timeToSubmitMs, questionRev },
  studentId,
) {
  const body = {
    question_id: questionId,
    student_response: studentResponse,
  };
  if (typeof timeToSubmitMs === 'number') body.time_to_submit_ms = timeToSubmitMs;
  if (typeof questionRev === 'string' && questionRev) body.question_rev = questionRev;
  const res = await axios.post(
    withStudent(`/sessions/${encodeURIComponent(sessionId)}/attempts`, studentId),
    body,
  );
  return res.data;
}

// ── Projects (list = read; submit = multipart EGRESS; poll = read) ───────────

/**
 * GET /careers/interview/projects?track= — list a track's active project briefs.
 * Returns `{ track, count, projects:[{ id, slug, track_slug, title, brief,
 * deliverable_kind }], next_steps:[str] }`. `deliverable_kind` ∈ ('xlsx',
 * 'pptx') tells the upload UX which file type to accept. The grading rubric is
 * NEVER surfaced. An empty list returns a "try another track" next-step.
 */
export async function listProjects(track, studentId) {
  const res = await axios.get(
    withStudent(`/projects?track=${encodeURIComponent(track)}`, studentId),
  );
  return res.data;
}

// The ONLY deliverable types the backend accepts (forge-proof kind detection is
// SERVER-authoritative off the ZIP central directory — the client `accept` +
// extension check is a fast, friendly pre-filter, NOT the security boundary).
export const ACCEPTED_ARTIFACT_EXT = Object.freeze({
  xlsx: '.xlsx',
  pptx: '.pptx',
});

/**
 * The HTML file-input `accept` attribute for a given deliverable kind, plus the
 * matching MIME types (so OS file pickers filter sensibly). The server is
 * authoritative; this just nudges the picker.
 */
export function acceptForKind(kind) {
  if (kind === 'xlsx') {
    return '.xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
  }
  if (kind === 'pptx') {
    return '.pptx,application/vnd.openxmlformats-officedocument.presentationml.presentation';
  }
  // Unknown/missing kind — accept both supported types rather than block.
  return '.xlsx,.pptx';
}

/**
 * Client-side extension guard (case-insensitive). Returns true when the file's
 * name ends in the expected `.xlsx`/`.pptx`. A fast, friendly pre-check so we
 * can show "please choose a .xlsx" WITHOUT a round-trip — but the SERVER's
 * forge-proof kind detection is the real authority (a renamed `.zip` passes
 * here yet is rejected server-side, surfaced as a calm 400).
 */
export function filenameMatchesKind(filename, kind) {
  if (typeof filename !== 'string' || !filename) return false;
  const ext = ACCEPTED_ARTIFACT_EXT[kind];
  if (!ext) return false;
  return filename.toLowerCase().endsWith(ext);
}

/**
 * POST /careers/interview/projects/{project_id}/submissions — multipart upload
 * of a real `.xlsx`/`.pptx` deliverable → schedules the rubric grade off the
 * request path (EGRESS; the under-13 freeze + daily artifact-grade budget apply
 * server-side). Returns 202 `{ submission_id, project_id, status, reused,
 * next_steps }`:
 *   - `reused:true` → an identical file was already graded; its scorecard is
 *     ready immediately (poll once to render it);
 *   - `reused:false` → a grade was scheduled; poll until 'graded'/'failed'.
 *
 * The file rides as multipart FormData under the `file` field (the backend's
 * `UploadFile = File(...)` param name). The OVER-BUDGET case is a 429 (cooldown
 * copy); a wrong file type is a 400 (calm "upload a .xlsx"); a storage outage is
 * a 503 (transient retry) — all map through `classifyCareerError` /
 * `friendlyError`.
 */
export async function uploadSubmission(projectId, file, studentId) {
  const form = new FormData();
  form.append('file', file);
  const res = await axios.post(
    withStudent(`/projects/${encodeURIComponent(projectId)}/submissions`, studentId),
    form,
    // Let the browser set the multipart boundary (do NOT hardcode
    // Content-Type — an explicit string without the boundary breaks parsing).
    { headers: { 'Content-Type': 'multipart/form-data' } },
  );
  return res.data;
}

/**
 * GET /careers/interview/submissions/{submission_id} — poll a submission's
 * status + (when graded) its evaluation. The frontend polls this
 * parsing→grading→graded (the PrepParseJob-poll precedent). Returns:
 *   { id, project_id, status, artifact_kind, artifact_byte_size, created_at,
 *     updated_at,
 *     job?: { status, progress_pct, error_message },
 *     evaluation?: { overall_score, dimension_scores, narrative,
 *                    verifier_status, graded_at, is_advisory:true,
 *                    needs_human_review },
 *     next_steps:[str] }
 *
 * `status` ∈ ('uploaded'|'parsing'|'grading'|'graded'|'failed'). The
 * `evaluation` is present ONLY when graded; it is ADVISORY PRACTICE FEEDBACK
 * (`is_advisory:true`), never an authoritative grade. IDOR-scoped server-side
 * (a foreign id → 404).
 */
export async function getSubmission(submissionId, studentId) {
  const res = await axios.get(
    withStudent(`/submissions/${encodeURIComponent(submissionId)}`, studentId),
  );
  return res.data;
}
